import React, { MutableRefObject } from 'react';
import { useSelector } from 'react-redux';
import { Routes } from 'constants/index';
import { getPath } from 'utils';
import { useLocation } from 'react-router';
import styled from 'styled-components/macro';
import { Sticky } from 'semantic-ui-react';
import { HeaderCombinedContainer } from 'containers/HeaderCombinedContainer';
import { SlideMobileHeaderMenuContainer } from 'components/App/lazy-imports';
import { OnlyMobile, OnlyDesktop } from 'components/atoms';
import { DefaultTheme, ThemedStyledProps } from 'styled-components';
import { SearchModalContainer } from '..';
import { burgerExpandedSelector } from 'modules/layouts/selectors';
import { BlogBanner } from 'components/molecules/BlogBanner/BlogBanner';
import { AskAssociationProvider } from 'components/molecules/AskAssociation/AskAssociationContext';

interface Props {
  children: any;
  contentRef?: MutableRefObject<HTMLDivElement | null>;
  contentInnerRef?: MutableRefObject<HTMLDivElement | null>;
  contentOnScroll?: () => void;
  fixedFooter?: any;
  className?: string;
  stickMobileHeader?: boolean;
  hideHeader?: boolean;
  isFeedPage?: boolean;
}

export const HeaderPageWrapper: React.FC<Props> = ({
  children,
  contentRef,
  contentOnScroll,
  fixedFooter,
  className,
  contentInnerRef,
  stickMobileHeader = true,
  // fix for Safari iphone hideHeader - force header to be removed if something needs to be on top of it
  hideHeader = false,
  isFeedPage = false,
}) => {
  const burgerExpanded = useSelector(burgerExpandedSelector);
  const location = useLocation();

  const isPageBlogFeed = getPath(Routes.BLOG_FEED) === location.pathname;

  const renderMobileHeader = React.useMemo((): React.ReactNode => {
    if (stickMobileHeader) {
      return (
        /* this div fixes header in ssr */
        <div>
          <StyledSticky context={contentRef}>
            <HeaderCombinedContainer />
          </StyledSticky>
          {isPageBlogFeed && !burgerExpanded && <BlogBanner />}
        </div>
      );
    }

    return (
      <>
        <HeaderCombinedContainer />
        {isPageBlogFeed && !burgerExpanded && <BlogBanner />}
      </>
    );
  }, [isPageBlogFeed, burgerExpanded, stickMobileHeader, contentRef]);

  const renderDesktopHeader = React.useMemo(
    (): React.ReactNode => (
      <>
        <HeaderCombinedContainer />
        {isPageBlogFeed && <BlogBanner />}
      </>
    ),
    [isPageBlogFeed]
  );

  return (
    <AskAssociationProvider>
      <Wrapper ref={contentRef} onScroll={contentOnScroll} className={className} isFeedPage={isFeedPage}>
        {/* this div fixes header in ssr */}
        <div>{!hideHeader && <OnlyMobile>{renderMobileHeader}</OnlyMobile>}</div>
        {/* this div fixes header in ssr */}

        <div>
          <OnlyDesktop>{renderDesktopHeader}</OnlyDesktop>
        </div>
        {burgerExpanded && <SlideMobileHeaderMenuContainer />}
        {!burgerExpanded && (
          <Container
            id="mainContent"
            ref={contentInnerRef}
            onScroll={contentOnScroll}
            stickMobileHeader={stickMobileHeader}
            tabIndex={0}
          >
            {children}
          </Container>
        )}
        {burgerExpanded ? null : fixedFooter}
      </Wrapper>
      <SearchModalContainer />
    </AskAssociationProvider>
  );
};

const Wrapper = styled.div<{ isFeedPage: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${props =>
    props.isFeedPage &&
    `
    height: 100%;
  `}

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    display: block;
  }

  ${props => props.theme.mediaQueries.desktopOnly} {
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  ${props => props.theme.mediaQueries.mobileOnly} {
    background: transparent;
    overflow: visible;
  }
`;

const StyledSticky = styled(Sticky)`
  .ui.sticky {
    overflow: hidden;
    -webkit-overflow-scrolling: touch;
  }
`;

const Container = styled.div<ThemedStyledProps<{ stickMobileHeader: boolean }, DefaultTheme>>`
  display: flex;
  flex-direction: column;
  flex: 1 1 100%;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    min-height: 65vh;
  }

  ${props => props.theme.mediaQueries.mobileOnly} {
    &&& {
      overflow-y: ${props => (props.stickMobileHeader ? 'auto' : 'unset')};
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
    }
  }
`;
